'use client';

import {
  SvgBookmarkFill,
  SvgChatbubbleTypingFill,
  SvgSend,
} from '@common/icon';
import { cn } from '@core/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { AuthQueryOptions, BOOKMARK_TYPE, UnauthQueryOptions } from '@/api';
import { openShareDrawer } from '@/app/_components/GlobalModal/_components/ShareDrawer';
import { SELECTOR } from '@/constants';
import { useBookmark } from '@/hooks/useBookmark';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { isBot } from '@/utils/isBot';

interface PolicyInfoCommentActionsProps {
  contentId: number;
  contentIdIdx: string;
  title: string;
}

export const PolicyInfoCommentActions = ({
  contentId,
  contentIdIdx,
  title,
}: PolicyInfoCommentActionsProps) => {
  const isLogin = useAuthStore((state) => state.isLogin);
  const { bookmark } = useBookmark();
  const { request: requestHaptic } = useHandleApp({
    type: MESSAGE_KEYWORD.HAPTIC,
  });

  const { data } = useQuery({
    ...(isLogin
      ? AuthQueryOptions[
          'get:/wello/v2/auth/contents/feedback/{bestContentsId}'
        ]
      : UnauthQueryOptions[
          'get:/wello/v2/allows/contents/best/feedback/{bestContentsId}'
        ])({
      path: { bestContentsId: contentId },
    }),
    enabled: isLogin !== undefined && !isBot(),
    placeholderData: keepPreviousData,
    select: ({ context }) => ({
      commentCount: context?.commentCount ?? 0,
      isBookmarked: context?.wishlistYN ?? false,
    }),
  });

  const commentCount = data?.commentCount ?? 0;
  const isBookmarked = data?.isBookmarked ?? false;

  const handleComment = () => {
    document
      .getElementById(SELECTOR.COMMENT_LIST_TOP)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleShare = () => {
    requestHaptic();
    openShareDrawer(window.location.href);
  };

  const handleBookmark = () => {
    bookmark({
      contentId,
      idx: contentIdIdx,
      isBookmarked,
      bookmarkType: BOOKMARK_TYPE.POLICY_INFO,
      contentName: title,
    });
  };

  return (
    <div className="flex h-20 w-full items-start justify-between">
      <div className="flex items-center gap-12">
        <button
          type="button"
          aria-label="댓글"
          className="group flex items-center gap-2"
          onClick={handleComment}
        >
          <SvgChatbubbleTypingFill className="size-20 text-Gray-400 transition-colors duration-150 group-hover:text-Gray-600" />
          <span className="font-600 text-Gray-600 typo-caption1">
            {commentCount}
          </span>
        </button>
        <button
          type="button"
          aria-label="공유"
          className="group flex items-center"
          onClick={handleShare}
        >
          <SvgSend className="size-20 text-Gray-400 transition-colors duration-150 group-hover:text-Gray-600" />
        </button>
      </div>
      <button
        type="button"
        aria-label={isBookmarked ? '북마크 해제' : '북마크'}
        className="group flex items-center"
        onClick={handleBookmark}
      >
        <SvgBookmarkFill
          className={cn(
            'size-20 text-Gray-400 transition-colors duration-150',
            {
              'text-B-500': isBookmarked,
              'group-hover:text-Gray-600': !isBookmarked,
            },
          )}
        />
      </button>
    </div>
  );
};
